import React from 'react';

import ExportProgressDialog, { ExportOperationStatus, ExportProgress } from './ExportProgressDialog';

export interface StudyExportFallback {
  // Button label, for example `Download original`.
  label: string;
  // Explains what failed and what the fallback does; shown with the error.
  message: string;
  // Text the user can copy into a support request.
  report?: string;
  operation: StudyExportOperation;
}

export interface StudyExportOperation {
  title: string;
  canCancel?: boolean;
  closeOnSuccess?: boolean;
  initialStage?: string;
  run: (signal: AbortSignal, reportProgress: (progress: ExportProgress) => void) => Promise<unknown>;
  // An alternative the user may start when `run` fails; return nothing when the failure has no alternative.
  getFallback?: (error: unknown) => StudyExportFallback | undefined;
}

interface ActiveExport extends StudyExportOperation {
  status: ExportOperationStatus;
  progress: ExportProgress;
  error?: string;
  fallback?: StudyExportFallback;
}

export type ExportDismissReason = 'handed-off' | 'closed';

export const useStudyExportController = (onDismiss?: (reason: ExportDismissReason) => void) => {
  const [activeExport, setActiveExport] = React.useState<ActiveExport | null>(null);
  const activeOperationId = React.useRef(0);
  const controller = React.useRef<AbortController>();
  const running = React.useRef(false);
  const onDismissRef = React.useRef(onDismiss);
  const closeOnSuccessRef = React.useRef(false);
  onDismissRef.current = onDismiss;

  const startExport = React.useCallback(async (operation: StudyExportOperation) => {
    if (running.current) return false;

    closeOnSuccessRef.current = Boolean(operation.closeOnSuccess);
    running.current = true;
    const operationId = activeOperationId.current + 1;
    activeOperationId.current = operationId;
    const nextController = new AbortController();
    controller.current = nextController;
    setActiveExport({
      ...operation,
      status: 'running',
      progress: { stage: operation.initialStage || 'Preparing export...' },
    });

    try {
      await operation.run(nextController.signal, (progress) => {
        if (activeOperationId.current === operationId && !nextController.signal.aborted) {
          setActiveExport((current) => (current ? { ...current, progress } : current));
        }
      });
      if (activeOperationId.current === operationId && !nextController.signal.aborted) {
        const handedOff = closeOnSuccessRef.current;
        setActiveExport((current) =>
          current?.closeOnSuccess ? null : current ? { ...current, status: 'success' } : current
        );
        if (handedOff) onDismissRef.current?.('handed-off');
      }
    } catch (error) {
      if (activeOperationId.current === operationId) {
        setActiveExport((current) =>
          current
            ? {
                ...current,
                status: nextController.signal.aborted ? 'cancelled' : 'error',
                error: error instanceof Error ? error.message : 'Export failed.',
                fallback: nextController.signal.aborted ? undefined : operation.getFallback?.(error),
              }
            : current
        );
      }
    } finally {
      if (activeOperationId.current === operationId) running.current = false;
    }
    return true;
  }, []);

  const cancelExport = React.useCallback(() => {
    controller.current?.abort();
    running.current = false;
    setActiveExport((current) => (current ? { ...current, status: 'cancelled' } : current));
  }, []);

  const closeExport = React.useCallback(() => {
    if (!running.current) {
      setActiveExport(null);
      onDismissRef.current?.('closed');
    }
  }, []);

  const retryExport = React.useCallback(() => {
    if (!activeExport || activeExport.status === 'running') return;
    const operation = activeExport;
    setActiveExport(null);
    void startExport(operation);
  }, [activeExport, startExport]);

  const startFallbackExport = React.useCallback(() => {
    const fallback = activeExport?.fallback;
    if (!fallback || activeExport?.status !== 'error') return;
    setActiveExport(null);
    void startExport(fallback.operation);
  }, [activeExport, startExport]);

  const exportProgressDialog = (
    <ExportProgressDialog
      open={Boolean(activeExport)}
      title={activeExport?.title || 'Export'}
      status={activeExport?.status || 'running'}
      progress={activeExport?.progress || { stage: 'Preparing export...' }}
      error={activeExport?.error}
      fallback={activeExport?.fallback}
      canCancel={Boolean(activeExport?.canCancel)}
      onCancel={cancelExport}
      onClose={closeExport}
      onRetry={retryExport}
      onFallback={startFallbackExport}
    />
  );

  return {
    startExport,
    exportProgressDialog,
    isExportRunning: activeExport?.status === 'running',
    isExportDialogOpen: Boolean(activeExport),
  };
};
